import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  CalendarDays, Clock, Video, Phone, MapPin, Link2, Mail, Globe, Copy, User, Building2,
  FileText, UserPlus, ListTodo, Save, ChevronRight, Sparkles, ClipboardList, Paperclip,
  Upload, CheckCircle2, CalendarPlus, Bell, Target, CircleDollarSign, Plus, Trash2, Loader2, ArrowLeft,
} from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { rdvDisplayStatus, StatusBadge, STATUS_DISPLAY, toHHMM, fmtShortDate } from "@/components/rdv/RdvTable";

const TYPE_LABELS = { exterieur: "Présentiel", telephonique: "Téléphone", visio: "Visio" };
const TYPE_ICONS = { exterieur: MapPin, telephonique: Phone, visio: Video };
const SOURCE_LABELS = { lien_reservation: "Lien de réservation", email: "Email", telephone: "Téléphone",
  agenda_interne: "Agenda interne", prospection: "Prospection", client_existant: "Client existant",
  agent_ia: "Agent IA", autre: "Autre" };
const TABS = [
  { id: "infos", label: "Informations" },
  { id: "cr", label: "Compte rendu" },
  { id: "taches", label: "Tâches associées" },
  { id: "historique", label: "Historique" },
  { id: "documents", label: "Documents" },
];
const STATUS_MENU = [
  { id: "nouveau", patch: { rdv_status: "a_venir" } },
  { id: "en_attente", patch: { booking_status: "a_reconfirmer" } },
  { id: "confirme", patch: { rdv_status: "confirme", booking_status: "confirme" } },
  { id: "annule", patch: { rdv_status: "annule" } },
  { id: "termine", patch: { rdv_status: "termine" } },
];
const HISTORY_ICONS = { creation: CalendarPlus, reservation: Globe, email: Mail, rdv: CheckCircle2,
  compte_rendu: ClipboardList, devis: FileText, conversion: UserPlus, ia: Sparkles };
const TASK_STATUS = { a_faire: "À faire", en_cours: "En cours", programmee: "Programmée", finalise: "Terminée", termine: "Terminée" };
const TASK_PRIORITY = { basse: "Faible", normale: "Moyenne", haute: "Haute", urgente: "Urgente" };
const PRIO_CLS = { urgente: "bg-rose-100 text-rose-800 border-rose-200", haute: "bg-amber-100 text-amber-800 border-amber-200",
  normale: "bg-slate-100 text-slate-600 border-slate-200", basse: "bg-slate-50 text-slate-500 border-slate-200" };

const Card = ({ title, icon: Icon, children, testid, action }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}{title}
      </h3>
      {action}
    </div>
    {children}
  </div>
);

const CopyBtn = ({ value, testid }) => (
  <button onClick={async () => { try { await navigator.clipboard.writeText(value); toast.success("Copié"); } catch {} }}
    className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-testid={testid}>
    <Copy className="w-3.5 h-3.5" />
  </button>
);

const fmtDateLong = (isoDate) => {
  try {
    return new Date(isoDate + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  } catch { return isoDate; }
};
const fmtDateTime = (isoStr) => {
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) + " — " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return isoStr; }
};
const durText = (min) => min >= 60 ? `${Math.floor(min / 60)} h${min % 60 ? String(min % 60).padStart(2, "0") : ""}` : `${min} min`;
const eur = (v) => `${Number(v).toLocaleString("fr-FR")} €`;

export default function RdvDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rdv, setRdv] = useState(null);
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("infos");
  const [notes, setNotes] = useState("");
  const [tasks, setTasks] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProposal, setAiProposal] = useState(null);
  const [crDraft, setCrDraft] = useState(null);
  const [selectedSteps, setSelectedSteps] = useState({});
  const [newTask, setNewTask] = useState({ title: "", deadline: "", priority: "normale" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const nowMin = now.getHours() * 60 + now.getMinutes();

  const load = () => {
    api.get(`/appointments/${id}`).then((r) => {
      setRdv(r.data);
      setNotes(r.data.description || "");
      const rs = r.data.report_sections || {};
      setCrDraft({ report: r.data.report || "", resume: rs.resume || "", points: (rs.points_cles || []).join("\n"),
        steps: (rs.prochaines_etapes || []).join("\n") });
    }).catch(() => { toast.error("Rendez-vous introuvable"); navigate("/rdv"); });
    api.get("/tasks").then((r) => setTasks((r.data || []).filter((t) => t.source_id === id && !t.archived))).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  };
  useEffect(load, [id]);

  const status = useMemo(() => rdv ? rdvDisplayStatus(rdv, today, nowMin) : "nouveau", [rdv, today, nowMin]);
  const isPast = rdv && (rdv.date < today || (rdv.date === today && rdv.start_min + (rdv.duration_minutes || 60) <= nowMin) || rdv.rdv_status === "termine");
  const hasReport = rdv && ((rdv.report || "").trim() || ["finalise", "envoye"].includes(rdv.report_status)
    || (rdv.report_sections?.resume || "").trim());

  const patch = async (body, msg) => {
    try {
      const r = await api.patch(`/appointments/${id}`, body);
      setRdv(r.data);
      if (msg) toast.success(msg);
      return r.data;
    } catch (e) { toast.error(apiError(e)); return null; }
  };

  const changeStatus = (s) => patch(s.patch, `Statut mis à jour : ${STATUS_DISPLAY[s.id]?.label}`);

  const saveNotes = async () => { setSaving(true); await patch({ description: notes }, "Enregistré"); setSaving(false); };

  const createQuote = async () => {
    if (!rdv.client_id && !rdv.prospect_id) { toast.error("Associez d'abord un client ou un prospect à ce RDV"); return; }
    try {
      const pot = rdv.potential || {};
      const r = await api.post("/accounting/quotes", {
        client_id: rdv.client_id || undefined, prospect_id: rdv.prospect_id || undefined,
        appointment_id: rdv.id, tva_mode: "non_assujetti",
        items: [{ prestation: pot.potential_project || rdv.title, qty: 1, price_ht: pot.estimated_value || 0 }],
      });
      toast.success(`Devis créé (brouillon) et relié au rendez-vous`);
      navigate(`/comptabilite?tab=devis&quote=${r.data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const convertToClient = async () => {
    if (!rdv.prospect_id) { toast.error("Aucun prospect lié à ce rendez-vous"); return; }
    try {
      const r = await api.post(`/prospects/${rdv.prospect_id}/convert`);
      await patch({ converted_client_id: r.data.client_id, client_id: r.data.client_id },
        r.data.existing ? "Client existant relié — historique conservé" : "Prospect converti en client");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const generateAi = async () => {
    setAiLoading(true);
    try {
      const r = await api.post(`/appointments/${id}/report/ai`, { notes: crDraft?.report || notes });
      setAiProposal(r.data);
      setCrDraft({ report: crDraft?.report || "", resume: r.data.resume || "",
        points: (r.data.points_cles || []).join("\n"), steps: (r.data.prochaines_etapes || []).join("\n") });
      setSelectedSteps(Object.fromEntries((r.data.taches_suggerees || []).map((t) => [t, true])));
      toast.success("Proposition IA générée — vérifiez avant d'enregistrer");
    } catch (e) { toast.error(apiError(e)); }
    setAiLoading(false);
  };

  const saveReport = async () => {
    setSaving(true);
    const rs = { resume: crDraft.resume, points_cles: crDraft.points.split("\n").map((s) => s.trim()).filter(Boolean),
      prochaines_etapes: crDraft.steps.split("\n").map((s) => s.trim()).filter(Boolean) };
    await patch({ report: crDraft.report, report_sections: rs,
      report_status: (crDraft.report.trim() || rs.resume || rs.points_cles.length) ? "finalise" : "brouillon" }, "Compte rendu enregistré");
    setSaving(false);
    load();
  };

  const createTasksFromSteps = async () => {
    const chosen = Object.entries(selectedSteps).filter(([, v]) => v).map(([t]) => t);
    if (!chosen.length) { toast.error("Sélectionnez au moins une étape"); return; }
    try {
      for (const t of chosen) {
        await api.post("/tasks", { title: t.slice(0, 160), client_id: rdv.client_id || null,
          deadline: rdv.date, service: "Commercial", priority: "haute",
          source_type: "appointment", source_id: rdv.id,
          source_label: `Rendez-vous — ${rdv.title}`, source_url: `/rdv/${rdv.id}` });
      }
      toast.success(`${chosen.length} tâche(s) créée(s) dans la To-do`);
      window.dispatchEvent(new Event("sf:tasks"));
      load();
    } catch { toast.error("Création impossible"); }
  };

  const addTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      await api.post("/tasks", { title: newTask.title.trim().slice(0, 160), client_id: rdv.client_id || null,
        deadline: newTask.deadline || rdv.date, priority: newTask.priority, service: "Commercial",
        source_type: "appointment", source_id: rdv.id, source_label: `Rendez-vous — ${rdv.title}`, source_url: `/rdv/${rdv.id}` });
      setNewTask({ title: "", deadline: "", priority: "normale" });
      toast.success("Tâche ajoutée");
      load();
    } catch { toast.error("Création impossible"); }
  };

  const toggleTask = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: t.status === "finalise" || t.status === "termine" ? "a_faire" : "finalise" }).catch(() => {});
    load();
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/appointments/${id}/attachments`, fd);
      toast.success("Document ajouté");
      load();
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
    e.target.value = "";
  };

  const deleteDoc = async (attId) => {
    await api.delete(`/appointments/${id}/attachments/${attId}`).catch(() => {});
    toast.success("Document supprimé");
    load();
  };

  if (!rdv) return <div className="py-20 text-center text-sm text-slate-400" data-testid="rdv-detail-loading">Chargement…</div>;

  const entity = rdv.company || rdv.prospect_name || rdv.contact_name || "—";
  const TypeIcon = TYPE_ICONS[rdv.type] || Video;
  const pot = rdv.potential || {};
  const history = [...(rdv.history || [])].sort((a, b) => (a.at || "").localeCompare(b.at || ""));

  return (
    <div className="space-y-5" data-testid="rdv-detail-page">
      {/* Breadcrumb + header */}
      <div>
        <nav className="flex items-center gap-1 text-xs text-slate-400 mb-2" data-testid="rdv-breadcrumb">
          <Link to="/rdv" className="hover:text-slate-700 transition-colors flex items-center gap-1"><ArrowLeft className="w-3 h-3" />Rendez-vous</Link>
          <ChevronRight className="w-3 h-3" /><span className="truncate max-w-[140px]">{entity}</span>
          <ChevronRight className="w-3 h-3" /><span className="text-slate-600 truncate max-w-[180px]">{rdv.title}</span>
        </nav>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900" data-testid="rdv-detail-title">{rdv.title}</h1>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="rdv-detail-status-menu" className="hover:opacity-80 transition-opacity" title="Changer le statut">
                    <StatusBadge status={status} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {STATUS_MENU.map((s) => (
                    <DropdownMenuItem key={s.id} onClick={() => changeStatus(s)} data-testid={`rdv-status-set-${s.id}`}>
                      {STATUS_DISPLAY[s.id]?.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <p className="text-sm text-slate-500 mt-1">{entity}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" className="rounded-xl gap-1.5 text-xs" onClick={createQuote} data-testid="rdv-action-devis">
              <FileText className="w-4 h-4" />Créer un devis
            </Button>
            {!rdv.client_id && rdv.prospect_id && (
              <Button variant="outline" className="rounded-xl gap-1.5 text-xs" onClick={convertToClient} data-testid="rdv-action-convert">
                <UserPlus className="w-4 h-4" />Convertir en client
              </Button>
            )}
            <Button variant="outline" className="rounded-xl gap-1.5 text-xs" onClick={() => setTab("taches")} data-testid="rdv-action-tasks">
              <ListTodo className="w-4 h-4" />Créer des tâches
            </Button>
            <Button className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl gap-1.5 text-xs" onClick={saveNotes} disabled={saving} data-testid="rdv-action-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer
            </Button>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="rdv-detail-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`rdv-detail-tab-${t.id}`}
            className={`px-3.5 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "infos" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
          <div className="lg:col-span-8 space-y-5">
            <Card title="Détails du rendez-vous" icon={CalendarDays} testid="rdv-info-details">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { icon: CalendarDays, label: "Date", main: fmtDateLong(rdv.date),
                    sub: new Date(rdv.date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long" }) },
                  { icon: Clock, label: "Heure", main: `${toHHMM(rdv.start_min)} – ${toHHMM(rdv.start_min + (rdv.rdv_minutes || 60))}`,
                    sub: durText(rdv.rdv_minutes || 60) },
                  { icon: TypeIcon, label: "Type", main: TYPE_LABELS[rdv.type] || rdv.type_label,
                    sub: rdv.visio_link ? (/meet\.google/i.test(rdv.visio_link) ? "Google Meet" : /zoom/i.test(rdv.visio_link) ? "Zoom" : /teams/i.test(rdv.visio_link) ? "Teams" : "Visio en ligne") : (rdv.address || "") },
                  { icon: Link2, label: "Source", main: SOURCE_LABELS[rdv.source] || (rdv.booked_online ? "Lien de réservation" : "Agenda interne"),
                    sub: rdv.booked_online ? "Calendrier en ligne" : "Ajout manuel" },
                ].map((d) => (
                  <div key={d.label} data-testid={`rdv-info-${d.label.toLowerCase()}`}>
                    <p className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider"><d.icon className="w-3.5 h-3.5" />{d.label}</p>
                    <p className="text-sm font-semibold text-slate-900 mt-1 capitalize">{d.main}</p>
                    {d.sub && <p className="text-xs text-slate-400 capitalize">{d.sub}</p>}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">
                    {(me?.name || "A").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                  <div><p className="text-[11px] text-slate-400 uppercase tracking-wider">Collaborateur</p>
                    <p className="text-sm font-medium text-slate-900" data-testid="rdv-info-collab">{me?.name || "—"}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center shrink-0">
                    {(rdv.contact_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                  <div><p className="text-[11px] text-slate-400 uppercase tracking-wider">Contact principal</p>
                    <p className="text-sm font-medium text-slate-900" data-testid="rdv-info-contact">{rdv.contact_name || "—"}</p></div>
                </div>
              </div>
              {(rdv.email || rdv.phone || rdv.website) && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                  {rdv.email && (
                    <p className="flex items-center gap-2 text-sm" data-testid="rdv-info-email">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a href={`mailto:${rdv.email}`} className="text-sky-700 hover:underline">{rdv.email}</a>
                      <CopyBtn value={rdv.email} testid="rdv-copy-email" />
                    </p>
                  )}
                  {rdv.phone && (
                    <p className="flex items-center gap-2 text-sm" data-testid="rdv-info-phone">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${rdv.phone}`} className="text-sky-700 hover:underline">{rdv.phone}</a>
                      <CopyBtn value={rdv.phone} testid="rdv-copy-phone" />
                    </p>
                  )}
                  {rdv.website && (
                    <p className="flex items-center gap-2 text-sm" data-testid="rdv-info-website">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <a href={/^https?:\/\//.test(rdv.website) ? rdv.website : `https://${rdv.website}`} target="_blank" rel="noreferrer"
                        className="text-sky-700 hover:underline">{rdv.website}</a>
                    </p>
                  )}
                </div>
              )}
            </Card>

            <Card title="Objet du rendez-vous" icon={FileText} testid="rdv-info-objet">
              <p className="text-sm text-slate-700">{rdv.title}</p>
            </Card>

            <Card title="Notes / Description" icon={ClipboardList} testid="rdv-info-notes"
              action={<button onClick={saveNotes} disabled={saving} data-testid="rdv-notes-save"
                className="text-xs font-semibold text-[#0369A1] hover:underline">{saving ? "Enregistrement…" : "Enregistrer"}</button>}>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="rdv-notes-input"
                placeholder="Informations saisies lors de la création ou de la réservation en ligne…"
                className="w-full text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-slate-200 resize-y" />
            </Card>
          </div>

          <div className="lg:col-span-4 space-y-5">
            <Card title="Profil client / prospect" icon={Building2} testid="rdv-side-profile">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-xl bg-purple-100 text-purple-700 text-sm font-bold flex items-center justify-center shrink-0">
                  {entity.slice(0, 2).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{entity}</p>
                  <span className={`text-[11px] font-medium border rounded-full px-2 py-0.5 ${rdv.client_id ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-purple-100 text-purple-800 border-purple-200"}`}
                    data-testid="rdv-profile-badge">{rdv.client_id ? "Client" : "Prospect"}</span>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                {rdv.client_id && (
                  <Link to={`/clients?focus=${rdv.client_id}`} data-testid="rdv-profile-see"
                    className="flex-1 text-center text-xs font-semibold border border-slate-200 rounded-xl px-3 py-2 hover:bg-slate-50 transition-colors">
                    Voir la fiche complète
                  </Link>
                )}
                {!rdv.client_id && rdv.prospect_id && (
                  <>
                    <Link to="/prospection" data-testid="rdv-profile-see-prospect"
                      className="flex-1 text-center text-xs font-semibold border border-slate-200 rounded-xl px-3 py-2 hover:bg-slate-50 transition-colors">
                      Voir la fiche complète
                    </Link>
                    <button onClick={convertToClient} data-testid="rdv-profile-convert"
                      className="flex-1 text-xs font-semibold bg-[#0B132B] text-white rounded-xl px-3 py-2 hover:bg-[#1C2A4F] transition-colors">
                      Convertir en client
                    </button>
                  </>
                )}
              </div>
            </Card>

            <Card title="Statut du rendez-vous" icon={Bell} testid="rdv-side-status">
              <ul className="space-y-3 text-sm">
                <li className="flex items-center justify-between">
                  <span className="text-slate-500 flex items-center gap-2"><Link2 className="w-3.5 h-3.5" />Source</span>
                  <span className="font-medium text-slate-900" data-testid="rdv-status-source">
                    {SOURCE_LABELS[rdv.source] || (rdv.booked_online ? "Lien de réservation" : "Agenda interne")}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-slate-500 flex items-center gap-2"><User className="w-3.5 h-3.5" />Participation</span>
                  <span className="font-medium text-slate-900">{(rdv.participants?.length || 0) + 1} participant(s)</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-slate-500 flex items-center gap-2"><Bell className="w-3.5 h-3.5" />Rappel envoyé</span>
                  <span className="font-medium text-slate-900" data-testid="rdv-status-reminder">{rdv.wa_reminder_sent ? "Oui" : "Non"}</span>
                </li>
              </ul>
            </Card>

            <Card title="Indicateurs & potentiel" icon={TrendingUpIcon} testid="rdv-side-potential"
              action={pot.ai_generated ? <span className="text-[10px] font-medium bg-purple-100 text-purple-700 border border-purple-200 rounded-full px-2 py-0.5">Estimation IA</span> : null}>
              {!pot.conversion_estimate && !pot.potential_project && !pot.estimated_value ? (
                <p className="text-sm text-slate-400" data-testid="rdv-potential-empty">Aucune estimation pour le moment — complétez le compte rendu ou générez une analyse IA.</p>
              ) : (
                <ul className="space-y-3.5">
                  {pot.conversion_estimate !== null && pot.conversion_estimate !== undefined && (
                    <li data-testid="rdv-potential-conversion">
                      <p className="text-[11px] text-slate-400 uppercase tracking-wider flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" />Conversion estimée</p>
                      <p className="text-xl font-bold text-slate-900 mt-0.5">{pot.conversion_estimate} %</p>
                    </li>
                  )}
                  {pot.potential_project && (
                    <li data-testid="rdv-potential-project">
                      <p className="text-[11px] text-slate-400 uppercase tracking-wider flex items-center gap-1.5"><Target className="w-3.5 h-3.5" />Projet potentiel</p>
                      <p className="text-sm font-semibold text-slate-900 mt-0.5">{pot.potential_project}</p>
                    </li>
                  )}
                  {pot.estimated_value ? (
                    <li data-testid="rdv-potential-value">
                      <p className="text-[11px] text-slate-400 uppercase tracking-wider flex items-center gap-1.5"><CircleDollarSign className="w-3.5 h-3.5" />Valeur estimée</p>
                      <p className="text-xl font-bold text-slate-900 mt-0.5">{eur(pot.estimated_value)}
                        {pot.estimated_duration && <span className="text-xs font-normal text-slate-400"> · {pot.estimated_duration}</span>}</p>
                    </li>
                  ) : null}
                </ul>
              )}
            </Card>

            <Card title="Actions rapides après ce rendez-vous" icon={Sparkles} testid="rdv-side-quick">
              <div className="space-y-2">
                {[
                  { label: "Créer un devis", Icon: FileText, onClick: createQuote, testid: "rdv-quick-devis" },
                  ...(!rdv.client_id && rdv.prospect_id ? [{ label: "Convertir en client", Icon: UserPlus, onClick: convertToClient, testid: "rdv-quick-convert" }] : []),
                  { label: "Planifier un suivi", Icon: CalendarPlus, onClick: () => navigate(`/rdv?followup=${rdv.id}`), testid: "rdv-quick-followup" },
                  { label: "Créer des tâches", Icon: ListTodo, onClick: () => setTab("taches"), testid: "rdv-quick-tasks" },
                ].map((a) => (
                  <button key={a.testid} onClick={a.onClick} data-testid={a.testid}
                    className="w-full flex items-center gap-2.5 text-left text-xs font-medium text-slate-700 border border-slate-200 rounded-xl px-3 py-2.5 hover:bg-slate-50 hover:border-slate-300 transition-all">
                    <span className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><a.Icon className="w-3.5 h-3.5 text-slate-600" /></span>
                    {a.label}
                  </button>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {tab === "cr" && crDraft && (
        <div className="max-w-3xl space-y-5">
          {isPast && !hasReport && (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3" data-testid="rdv-cr-warning">
              <span className="text-amber-600 text-lg">⚠</span>
              <p className="flex-1 text-sm text-amber-800">Le compte rendu n'a pas encore été complété. Pensez à le remplir après le rendez-vous.</p>
            </div>
          )}
          <Card title="Compte rendu" icon={ClipboardList} testid="rdv-cr-card"
            action={<Button variant="outline" className="rounded-xl gap-1.5 text-xs" onClick={generateAi} disabled={aiLoading} data-testid="rdv-cr-ai">
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-purple-600" />}
              Générer / structurer avec l'IA
            </Button>}>
            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Notes brutes</label>
                <textarea value={crDraft.report} onChange={(e) => setCrDraft({ ...crDraft, report: e.target.value })} rows={3}
                  data-testid="rdv-cr-report" placeholder="Notes prises pendant le rendez-vous…"
                  className="mt-1 w-full text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-slate-200 resize-y" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Résumé du rendez-vous</label>
                <textarea value={crDraft.resume} onChange={(e) => setCrDraft({ ...crDraft, resume: e.target.value })} rows={2}
                  data-testid="rdv-cr-resume" placeholder="Résumé de l'échange…"
                  className="mt-1 w-full text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-slate-200 resize-y" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Points clés abordés <span className="normal-case font-normal text-slate-400">(un par ligne)</span></label>
                  <textarea value={crDraft.points} onChange={(e) => setCrDraft({ ...crDraft, points: e.target.value })} rows={4}
                    data-testid="rdv-cr-points" placeholder={"Besoins et objectifs du client\nBudget et délai\nContraintes…"}
                    className="mt-1 w-full text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-slate-200 resize-y" />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Prochaines étapes <span className="normal-case font-normal text-slate-400">(un par ligne)</span></label>
                  <textarea value={crDraft.steps} onChange={(e) => setCrDraft({ ...crDraft, steps: e.target.value })} rows={4}
                    data-testid="rdv-cr-steps" placeholder={"Envoyer la proposition commerciale\nCréer le devis\nPlanifier un nouveau rendez-vous…"}
                    className="mt-1 w-full text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-slate-200 resize-y" />
                </div>
              </div>
              {aiProposal?.taches_suggerees?.length > 0 && (
                <div className="bg-purple-50/60 border border-purple-200 rounded-xl p-4" data-testid="rdv-cr-ai-tasks">
                  <p className="text-xs font-semibold text-purple-800 flex items-center gap-1.5 mb-2"><Sparkles className="w-3.5 h-3.5" />Tâches suggérées par l'IA — sélectionnez puis créez</p>
                  <ul className="space-y-1.5">
                    {aiProposal.taches_suggerees.map((t) => (
                      <li key={t}>
                        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input type="checkbox" checked={!!selectedSteps[t]} data-testid={`rdv-cr-task-check-${t.slice(0, 20).replace(/\W+/g, "-")}`}
                            onChange={(e) => setSelectedSteps({ ...selectedSteps, [t]: e.target.checked })}
                            className="rounded border-slate-300" />
                          {t}
                        </label>
                      </li>
                    ))}
                  </ul>
                  <Button className="mt-3 bg-purple-700 hover:bg-purple-800 text-white rounded-xl gap-1.5 text-xs" onClick={createTasksFromSteps} data-testid="rdv-cr-create-tasks">
                    <ListTodo className="w-4 h-4" />Créer les tâches
                  </Button>
                </div>
              )}
              <div className="flex justify-end">
                <Button className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl gap-1.5 text-xs" onClick={saveReport} disabled={saving} data-testid="rdv-cr-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer le compte rendu
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {tab === "taches" && (
        <Card title="Tâches associées" icon={ListTodo} testid="rdv-tasks-card"
          action={<span className="text-xs text-slate-400">{tasks.length} tâche(s)</span>}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-left">
              <thead>
                <tr className="border-b border-slate-100">
                  {["Tâche", "Échéance", "Statut", "Priorité", ""].map((h) => (
                    <th key={h} className="px-2 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tasks.length === 0 && (
                  <tr><td colSpan={5} className="px-2 py-8 text-center text-sm text-slate-400" data-testid="rdv-tasks-empty">Aucune tâche associée pour l'instant.</td></tr>
                )}
                {tasks.map((t) => (
                  <tr key={t.id} className="border-b border-slate-50 last:border-0" data-testid={`rdv-task-${t.id}`}>
                    <td className="px-2 py-2.5 text-sm font-medium text-slate-800">{t.title}</td>
                    <td className="px-2 py-2.5 text-sm text-slate-500 whitespace-nowrap">{t.deadline ? fmtShortDate(t.deadline) : "—"}</td>
                    <td className="px-2 py-2.5">
                      <button onClick={() => toggleTask(t)} data-testid={`rdv-task-toggle-${t.id}`}
                        className={`text-[11px] font-medium border rounded-full px-2 py-0.5 ${
                          ["finalise", "termine"].includes(t.status) ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-sky-100 text-sky-800 border-sky-200"}`}>
                        {TASK_STATUS[t.status] || t.status}
                      </button>
                    </td>
                    <td className="px-2 py-2.5">
                      <span className={`text-[11px] font-medium border rounded-full px-2 py-0.5 ${PRIO_CLS[t.priority] || PRIO_CLS.normale}`}>
                        {TASK_PRIORITY[t.priority] || t.priority || "Moyenne"}
                      </span>
                    </td>
                    <td className="px-2 py-2.5 text-right">
                      <Link to="/taches" className="text-xs font-semibold text-[#0369A1] hover:underline">To-do ›</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100">
            <input value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} data-testid="rdv-task-new-title"
              placeholder="Intitulé de la tâche…" className="flex-1 min-w-[180px] h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs focus:outline-none focus:ring-2 focus:ring-slate-200" />
            <input type="date" value={newTask.deadline} onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })} data-testid="rdv-task-new-deadline"
              className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-slate-200" />
            <select value={newTask.priority} onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })} data-testid="rdv-task-new-priority"
              className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-slate-200">
              <option value="basse">Faible</option><option value="normale">Moyenne</option>
              <option value="haute">Haute</option><option value="urgente">Urgente</option>
            </select>
            <Button className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl gap-1.5 text-xs" onClick={addTask} data-testid="rdv-task-add">
              <Plus className="w-4 h-4" />Ajouter une tâche
            </Button>
          </div>
        </Card>
      )}

      {tab === "historique" && (
        <Card title="Historique" icon={Clock} testid="rdv-history-card">
          {history.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center" data-testid="rdv-history-empty">Aucun événement pour le moment.</p>
          ) : (
            <ol className="relative border-l border-slate-200 ml-2 space-y-4 py-1">
              {history.map((h) => {
                const Icon = HISTORY_ICONS[h.kind] || Clock;
                return (
                  <li key={h.id} className="ml-5" data-testid={`rdv-history-${h.id}`}>
                    <span className="absolute -left-2.5 w-5 h-5 rounded-full bg-white border border-slate-200 flex items-center justify-center">
                      <Icon className="w-3 h-3 text-slate-500" />
                    </span>
                    <p className="text-sm font-medium text-slate-800">{h.label}</p>
                    <p className="text-xs text-slate-400">{fmtDateTime(h.at)}</p>
                  </li>
                );
              })}
            </ol>
          )}
        </Card>
      )}

      {tab === "documents" && (
        <Card title="Documents" icon={Paperclip} testid="rdv-docs-card"
          action={<label className="cursor-pointer text-xs font-semibold text-[#0369A1] hover:underline flex items-center gap-1.5" data-testid="rdv-doc-upload-label">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}Ajouter un document
            <input type="file" className="hidden" onChange={uploadDoc} data-testid="rdv-doc-upload" />
          </label>}>
          {rdv.quote_id && (
            <a href={`${API}/api/accounting/quotes/${rdv.quote_id}/pdf`} target="_blank" rel="noreferrer" data-testid="rdv-doc-quote"
              className="flex items-center gap-3 border border-slate-200 rounded-xl px-3 py-2.5 mb-2 hover:bg-slate-50 transition-colors">
              <FileText className="w-4 h-4 text-rose-500" />
              <span className="flex-1 text-sm font-medium text-slate-800">Devis créé suite au rendez-vous (PDF)</span>
            </a>
          )}
          {(rdv.attachments || []).length === 0 && !rdv.quote_id ? (
            <p className="text-sm text-slate-400 py-6 text-center" data-testid="rdv-docs-empty">Aucun document lié à ce rendez-vous.</p>
          ) : (
            <ul className="space-y-2">
              {(rdv.attachments || []).map((att) => (
                <li key={att.id} className="flex items-center gap-3 border border-slate-200 rounded-xl px-3 py-2.5" data-testid={`rdv-doc-${att.id}`}>
                  <Paperclip className="w-4 h-4 text-slate-400 shrink-0" />
                  <a href={`${API}/api/appointments/${rdv.id}/attachments/${att.id}`} target="_blank" rel="noreferrer"
                    className="flex-1 text-sm font-medium text-slate-800 hover:text-sky-700 truncate">{att.name}</a>
                  <span className="text-xs text-slate-400">{att.size >= 1048576 ? `${(att.size / 1048576).toFixed(1)} Mo` : `${Math.round(att.size / 1024)} Ko`}</span>
                  <button onClick={() => deleteDoc(att.id)} className="p-1 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                    data-testid={`rdv-doc-delete-${att.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}

function TrendingUpIcon(props) {
  return <CheckCircle2 {...props} />;
}
